import { useForm } from 'react-hook-form';
import { columns } from '../columns';
import { shippingSelfQuery } from '../../../services/react-query/optionMasterQuery';
import { useGetScanDelivery } from './useGetScanDelivery';
import { useCallback, useRef, useState } from 'preact/hooks';
import { openAlert } from '../../../components/alert/hooks';
import { useUpdateTracking } from './useUpdateTracking';
import { useModal } from '../../../components/modal/hooks/useModal';

export const useGetScan = () => {
  const [scanData, setScanData] = useState({
    claim: null,
  });
  const [isClaimValidated, setIsClaimValidated] = useState(false);
  const [tableData, setTableData] = useState([]);
  const { open: openImportExcel, onToggle: onToggleImportExcel } = useModal();
  const { control, setValue, getValues, reset, handleSubmit } = useForm();
  const { data: shippingSelfOptions } = shippingSelfQuery();
  const { mutate: getScanDelivery, isPending: loadingScanDelivery } = useGetScanDelivery({
    reset,
    getValues,
  });
  const { mutate: updateTracking, isPending: loadingUpdateTracking } = useUpdateTracking();

  const trackingInputRef = useRef(null);
  const claimCodeInputRef = useRef(null);

  const handleOnEnter = useCallback(
    async (event, fieldName) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        const value = getValues(fieldName);

        switch (fieldName) {
          case 'claimCode':
            if (value) {
              const isDuplicate = tableData.some((item) => item.claimCode === value);

              if (isDuplicate) {
                openAlert({
                  type: 'error',
                  message: 'เลขที่เคลมซ้ำ ไม่สามารถเพิ่มข้อมูลได้',
                });
                reset({
                  claimCode: '',
                });
                claimCodeInputRef.current?.focus();
                return;
              }

              getScanDelivery(value, {
                onSuccess: (dataScan) => {
                  const claim = dataScan?.claim;
                  setScanData((prev) => ({
                    ...prev,
                    claim,
                  }));

                  setIsClaimValidated(true);

                  setValue('claimCode', getValues('claimCode'));
                  setValue('shippingId', claim?.shippingId || getValues('shippingId'));
                  setValue('trackingNumber', claim?.trackingNumber);

                  trackingInputRef.current?.focus();
                },
                onError: () => {
                  setIsClaimValidated(false);
                  reset({
                    claimCode: '',
                  });
                },
              });
            }
            break;
          case 'trackingNumber':
            if (!isClaimValidated) {
              openAlert({
                type: 'error',
                message: 'กรุณากรอกและตรวจสอบเลขที่เคลมก่อน',
              });
              claimCodeInputRef.current?.focus();
              return;
            }
            if (value) {
              const isDup = tableData.some((item) => item.trackingNumber === value);

              if (isDup) {
                openAlert({
                  type: 'error',
                  message: 'เลขที่พัสดุซ้ำ ไม่สามารถเพิ่มข้อมูลได้',
                });
                setValue('trackingNumber', '');
                trackingInputRef.current?.focus();
                return;
              }

              const claim = scanData.claim;
              if (!claim?.claimId) {
                openAlert({
                  type: 'error',
                  message: 'ไม่พบข้อมูลเคลม กรุณากรอกเลขที่เคลมและกด Enter ใหม่',
                });
                return;
              }

              const newTableRow = {
                key: tableData.length + 1,
                claimId: claim.claimId,
                claimCode: getValues('claimCode'),
                fullName: claim.fullName,
                telephone: claim.telephone,
                mobileModelName: claim.mobileModelName,
                categoryCode: claim.categoryCode,
                trackingNumber: value,
                shippingId: getValues('shippingId'),
              };

              setTableData([newTableRow, ...tableData]);
              setIsClaimValidated(false);

              reset({
                shippingId: getValues('shippingId'),
                claimCode: '',
                trackingNumber: '',
              });
              claimCodeInputRef.current?.focus();
            }
            break;
        }
      }
    },
    [getScanDelivery, getValues, scanData, tableData, reset, isClaimValidated],
  );

  const onDeleteRecordScanDelivery = (record) => {
    const newTableData = tableData.filter((item) => item.key !== record.key);

    setTableData(newTableData);

    claimCodeInputRef.current?.focus();
  };

  const onSubmit = () => {
    if (tableData.length === 0) {
      openAlert({
        type: 'error',
        message: 'กรุณาเพิ่มข้อมูลอย่างน้อย 1 รายการ',
      });
      return;
    }

    const payload = tableData?.map((item) => ({
      claimId: item?.claimId,
      claimCode: item?.claimCode,
      trackingNumber: item?.trackingNumber,
      shippingId: item?.shippingId,
    }));

    updateTracking(payload, {
      onSuccess: () => {
        setTableData([]);
        setIsClaimValidated(false);
        reset({
          claimCode: '',
          trackingNumber: '',
        });
      },
    });
  };

  const columnData = columns({ onDeleteRecordScanDelivery });
  const formUpdateObj = useForm({ defaultValues: {} });

  return {
    control,
    setValue,
    getValues,
    columnData,
    handleOnEnter,
    formUpdateObj,
    shippingSelfOptions,
    trackingInputRef,
    claimCodeInputRef,
    onSubmit,
    handleSubmit,
    loadingScanDelivery,
    tableData,
    loadingUpdateTracking,
    openImportExcel,
    onToggleImportExcel,
    isClaimValidated,
  };
};
